import { forwardRef } from "react";
import {
  differenceInDays,
  format,
  getDate,
  getMonth,
  getYear,
  isValid,
  parseISO,
  subDays,
} from "date-fns";

type DataContextBarProps = {
  appliedFilters: Record<string, string | number>;
  // Optional "Download view" action, rendered at the end of this bar. Lives
  // here rather than in the page header because this is the one row that is
  // already about the data currently on screen — and it only renders when
  // there are widgets to capture, which is the same condition that gates
  // this bar.
  onDownloadView?: () => void;
  isDownloadingView?: boolean;
};

// Returns "end of Q1" … "end of Q4" if `date` is the last day of that quarter.
function getEndOfQuarterLabel(date: Date): string {
  const m = getMonth(date); // 0-indexed
  const d = getDate(date);
  if (m === 2 && d === 31) return "end of Q1";
  if (m === 5 && d === 30) return "end of Q2";
  if (m === 8 && d === 30) return "end of Q3";
  if (m === 11 && d === 31) return "end of Q4";
  return "";
}

// Returns "Q1 2025" etc. if [start, end] is exactly a full calendar quarter.
function getFullQuarterLabel(start: Date, end: Date): string {
  if (getYear(start) !== getYear(end)) return "";
  const year = getYear(end);
  const sm = getMonth(start);
  const sd = getDate(start);
  const em = getMonth(end);
  const ed = getDate(end);
  if (sm === 0 && sd === 1 && em === 2 && ed === 31) return `Q1 ${year}`;
  if (sm === 3 && sd === 1 && em === 5 && ed === 30) return `Q2 ${year}`;
  if (sm === 6 && sd === 1 && em === 8 && ed === 30) return `Q3 ${year}`;
  if (sm === 9 && sd === 1 && em === 11 && ed === 31) return `Q4 ${year}`;
  return "";
}

// Returns "2025" if [start, end] is exactly a full calendar year.
function getFullYearLabel(start: Date, end: Date): string {
  if (
    getMonth(start) === 0 &&
    getDate(start) === 1 &&
    getMonth(end) === 11 &&
    getDate(end) === 31 &&
    getYear(start) === getYear(end)
  ) {
    return String(getYear(end));
  }
  return "";
}

// Renders the data-context info bar shown below the filter bar.
// Shows the current data period end date and computes the prior period
// as the same duration immediately before the current start date.
// forwardRef so the page can hand this row to the dashboard screenshot, which
// clones it in above the widgets — the PNG then carries the same data-period
// line the user is looking at.
const DataContextBar = forwardRef<HTMLParagraphElement, DataContextBarProps>(
  function DataContextBar(
    { appliedFilters, onDownloadView, isDownloadingView = false },
    ref,
  ) {
    const startStr = String(appliedFilters.start_date ?? "");
    const endStr = String(appliedFilters.end_date ?? "");

    if (!startStr || !endStr) return null;

    const startDate = parseISO(startStr);
    const endDate = parseISO(endStr);

    if (!isValid(startDate) || !isValid(endDate) || endDate < startDate)
      return null;

    // Prior period: same duration, ending the day before current start.
    const durationDays = differenceInDays(endDate, startDate);
    const priorEnd = subDays(startDate, 1);
    const priorStart = subDays(priorEnd, durationDays);

    const fmt = (d: Date) => format(d, "MMM d, yyyy");

    const endOfQuarter = getEndOfQuarterLabel(endDate);
    const priorPeriodLabel =
      getFullQuarterLabel(priorStart, priorEnd) ||
      getFullYearLabel(priorStart, priorEnd);

    return (
      <p
      ref={ref}
      className="dashboard-context-bar d-flex align-items-center gap-1 mt-1 mb-2 small"
    >
        <i className="bi bi-filter fs-5" />
        <span>
          Data as of {fmt(startDate)} &ndash; {fmt(endDate)}
          {endOfQuarter ? ` (${endOfQuarter})` : ""}. &ldquo;Prior&rdquo; ={" "}
          {fmt(priorStart)} &ndash; {fmt(priorEnd)}
          {priorPeriodLabel
            ? ` (${priorPeriodLabel} · previous period)`
            : " (previous period)"}
          .{" "}
          <span className="bg-dark bg-opacity-10 px-3 py-1 text-primary rounded-5 fw-medium">
            Weekends and federal holidays excluded from business-day
            calculations.
          </span>{" "}
        </span>
        {onDownloadView && (
          <button
            type="button"
            className="btn btn-sm btn-primary ms-auto flex-shrink-0 d-flex align-items-center gap-1"
            onClick={onDownloadView}
            disabled={isDownloadingView}
            title="Download every widget on this dashboard as one PNG"
            // This row is cloned into the screenshot, and the button that starts
            // the capture has no business appearing inside it.
            data-html2canvas-ignore="true"
          >
            <i
              className={`bi ${
                isDownloadingView ? "bi-hourglass-split" : "bi-image"
              }`}
              aria-hidden="true"
            />
            {isDownloadingView ? "Capturing…" : "Download view"}
          </button>
        )}
      </p>
    );
  },
);

export default DataContextBar;
